/**
 * Locale-aware date formatting on top of date-fns.
 *
 * The app historically called `format(date, "MMM d, yyyy")` / `"h:mm a"`
 * inline. Those patterns are US-only (month-first, 12-hour). This module
 * maps a small set of *semantic* styles to the right pattern per locale,
 * so components say what they mean ("date", "time", "dateTime") and the
 * locale decides how it looks:
 *
 *   style        en-US                      es-ES
 *   date         Oct 6, 2026                6 oct 2026
 *   dateShort    Oct 6                      6 oct
 *   dateLong     October 6, 2026            6 de octubre de 2026
 *   weekday      Tuesday, October 6         martes, 6 de octubre
 *   weekdayLong  Tuesday, October 6, 2026   martes, 6 de octubre de 2026
 *   time         2:30 PM                    14:30
 *   dateTime     Oct 6, 2026 2:30 PM        6 oct 2026, 14:30
 *   dateTimeLong October 6, 2026 at 2:30 PM 6 de octubre de 2026 a las 14:30
 *   monthYear    October 2026               octubre de 2026
 *   monthShort   Oct 2026                   oct 2026
 *   iso          2026-10-06                 2026-10-06   (machine value, never localized)
 *
 * Prefer `useDateFormat()` from `@/contexts/LocaleContext` inside components
 * (it follows the current language). Use `formatDateStyle` directly only in
 * non-React code that already knows the locale.
 */

import { format as dfFormat, type Locale } from "date-fns";
import { es } from "date-fns/locale";

export type DateStyle =
  | "date"
  | "dateShort"
  | "dateLong"
  | "weekday"
  | "weekdayLong"
  | "time"
  | "dateTime"
  | "dateTimeLong"
  | "monthYear"
  | "monthShort"
  | "iso";

type PatternTable = Record<DateStyle, string>;

const EN_US: PatternTable = {
  date: "MMM d, yyyy",
  dateShort: "MMM d",
  dateLong: "MMMM d, yyyy",
  weekday: "EEEE, MMMM d",
  weekdayLong: "EEEE, MMMM d, yyyy",
  time: "h:mm a",
  dateTime: "MMM d, yyyy h:mm a",
  dateTimeLong: "MMMM d, yyyy 'at' h:mm a",
  monthYear: "MMMM yyyy",
  monthShort: "MMM yyyy",
  iso: "yyyy-MM-dd",
};

const ES_ES: PatternTable = {
  date: "d MMM yyyy",
  dateShort: "d MMM",
  dateLong: "d 'de' MMMM 'de' yyyy",
  weekday: "EEEE, d 'de' MMMM",
  weekdayLong: "EEEE, d 'de' MMMM 'de' yyyy",
  time: "HH:mm",
  dateTime: "d MMM yyyy, HH:mm",
  dateTimeLong: "d 'de' MMMM 'de' yyyy 'a las' HH:mm",
  monthYear: "MMMM 'de' yyyy",
  monthShort: "MMM yyyy",
  iso: "yyyy-MM-dd",
};

const TABLES: Record<string, PatternTable> = {
  "en-US": EN_US,
  "es-ES": ES_ES,
};

/** date-fns locale object for a BCP-47 tag (undefined = date-fns default, en-US). */
export function getDateFnsLocale(locale: string | null | undefined): Locale | undefined {
  if (!locale) return undefined;
  return locale.toLowerCase().startsWith("es") ? es : undefined;
}

/** The raw pattern for a style in a locale — for the rare caller that needs it. */
export function getDatePattern(style: DateStyle, locale: string | null | undefined): string {
  const table = (locale && TABLES[locale]) || (locale?.toLowerCase().startsWith("es") ? ES_ES : EN_US);
  return table[style];
}

/** Does this locale use a 24-hour clock? (drives time pickers / AM-PM toggles) */
export function uses24HourClock(locale: string | null | undefined): boolean {
  return !!locale && locale.toLowerCase().startsWith("es");
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Format a date in a semantic style for a locale.
 * Invalid dates return "" instead of throwing (date-fns throws on `Invalid Date`).
 */
export function formatDateStyle(
  value: Date | string | number | null | undefined,
  style: DateStyle,
  locale: string | null | undefined = "en-US",
): string {
  if (value === null || value === undefined || value === "") return "";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return dfFormat(d, getDatePattern(style, locale), { locale: getDateFnsLocale(locale) });
}

/**
 * Format with an explicit date-fns pattern but the locale's month/day names.
 * Use when a style above doesn't fit (e.g. calendar grid headers "EEE").
 */
export function formatWithPattern(
  value: Date | string | number | null | undefined,
  pattern: string,
  locale: string | null | undefined = "en-US",
): string {
  if (value === null || value === undefined || value === "") return "";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return dfFormat(d, pattern, { locale: getDateFnsLocale(locale) });
}
